/*
MIT License

Copyright (c) 2018 Shopify, 2023 VRIFY Technologies Inc.

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import type { MirrorAttachedEvent, MirrorDestroyEvent } from '@shopify/draggable'
import { Draggable, Sortable } from '@shopify/draggable'
import { MultiDragSensor } from '@/lib/editor/multi-drag-sensor'
import type { MultiNodeNode } from '@/lib/editor/multi-node-node'
import { insertMultiNodeBefore } from '@/lib/editor/multi-node-node'

type DraggableOptions = ConstructorParameters<typeof Draggable>[1]

export class MultiDragSortable extends Sortable {
  // @ts-ignore
  originalSource!: Node | MultiNodeNode<Node>
  mirror?: any
  destinationPlaceholderShowing: boolean
  constructor(containers: HTMLElement, options?: DraggableOptions & { excludeHandle?: string }) {
    super(containers, {
      ...options,
      // @ts-ignore
      sensors: [MultiDragSensor],
      exclude: {
        // @ts-ignore
        sensors: [Draggable.Sensors.MouseSensor],
      },
    })
    this.destinationPlaceholderShowing = false
    containers.insertBefore = insertMultiNodeBefore

    this.on('mirror:attached', this.attachMultiDragNumberIndicator.bind(this))
    this.on('mirror:attached', this.attachMultiCardClasses)

    this.on('mirror:destroy', this.removeMultiCardClasses)

    this.on('drag:stop', () => {
      this.destinationPlaceholderShowing = false
    })

    this.on('drag:move', this.styleDestinationPlaceholder.bind(this))
  }

  getSortableElementsForContainer(container: ParentNode): HTMLElement[] {
    // @ts-ignore
    const allSortableElements = container.querySelectorAll<HTMLElement>(this.options.draggable)

    return Array.from(allSortableElements).filter(childElement => {
      const multiNodeElements = this.originalSource && 'elements' in this.originalSource && this.originalSource.elements
      const parentNode = childElement.parentNode as HTMLElement
      const isAllowedParentContainer =
        (parentNode === container || parentNode.classList.contains('tile-wrapper')) && parentNode !== this.mirror
      return (
        childElement !== this.originalSource &&
        childElement !== this.mirror &&
        !childElement.classList.contains('tile-wrapper') &&
        (!multiNodeElements || !multiNodeElements.includes(childElement)) &&
        isAllowedParentContainer
      )
    })
  }
  override getDraggableElementsForContainer(container: HTMLElement): HTMLElement[] {
    return this.getSortableElementsForContainer(container)
  }

  setSelection(elements: Element[]) {
    // @ts-ignore
    const multiDragSensor = this.sensors.find(sensor => sensor instanceof MultiDragSensor) as MultiDragSensor
    if (multiDragSensor) {
      multiDragSensor.setSelection(elements)
    }
  }

  private attachMultiDragNumberIndicator(event: MirrorAttachedEvent) {
    const mirror = event?.mirror
    // @ts-ignore
    const numberOfElements = event.originalSource.elements.length

    if (numberOfElements > 1) {
      const numberIndicator = document.createElement('div')

      numberIndicator.style.width = '36px'
      numberIndicator.style.height = '36px'
      numberIndicator.style.borderRadius = '50%'
      numberIndicator.style.backgroundColor = '#2F5A62'
      numberIndicator.style.border = '2px solid #97ACB0'
      numberIndicator.style.position = 'absolute'
      numberIndicator.style.top = '-8px'
      numberIndicator.style.right = '-8px'
      numberIndicator.style.display = 'flex'
      numberIndicator.style.justifyContent = 'center'
      numberIndicator.style.alignItems = 'center'
      numberIndicator.style.fontSize = '14px'
      numberIndicator.style.fontWeight = '600'
      numberIndicator.style.zIndex = '999'

      numberIndicator.textContent = `${numberOfElements}`

      numberIndicator.classList.add('numberIndicator')

      this.hideExtraElements()

      mirror.appendChild(numberIndicator)
    }
  }

  /**
   * Responsible for setting the classes that style the multi-card look when dragging. Styles exist in base.css
   */
  private attachMultiCardClasses(event: MirrorAttachedEvent) {
    // @ts-ignore - "for now" - James N. Oct 5, 2023
    if (event.originalSource.elements.length > 2) {
      event.mirror.classList.add('multi-card')
    }

    // @ts-ignore - "for now" James N. Oct 5, 2023
    if (event.originalSource.elements.length > 1) {
      event.mirror.classList.add('double-card')
    }
  }

  /**
   * Responsible for hiding additionally dragged elements in the Draggable ghost
   */
  private hideExtraElements() {
    const tilesExceptFirst = document.body.querySelectorAll(
      // @ts-ignore
      `.tile-wrapper > ${this.options.draggable} + ${this.options.draggable}`
    ) as NodeListOf<HTMLElement>

    if (tilesExceptFirst.length) {
      tilesExceptFirst.forEach(el => {
        el.style.display = 'none'
      })
    }
  }

  private removeMultiCardClasses(event: MirrorDestroyEvent) {
    event.mirror.classList.remove('multi-card')
    event.mirror.classList.remove('double-card')
  }

  /**
   * Responsible for styling the placeholder for where the user is going to drop their currently dragged slide(s)
   */
  private styleDestinationPlaceholder() {
    const destinationSlide = document.querySelector('.draggable-source--is-dragging') as HTMLElement

    if (destinationSlide && !this.destinationPlaceholderShowing) {
      const originalHeight = destinationSlide.offsetHeight
      const originalWidth = destinationSlide.offsetWidth
      const placeholderOverlay = document.createElement('div')

      placeholderOverlay.style.border = '1px dashed #D3EFEC'
      placeholderOverlay.style.height = `${originalHeight}px`
      placeholderOverlay.style.width = `${originalWidth}px`
      placeholderOverlay.style.borderRadius = '0.5rem'
      placeholderOverlay.style.background = 'rgba(211, 239, 236, 0.20)'
      placeholderOverlay.style.boxShadow = '0px 2px 2px 0px rgba(0, 0, 0, 0.25)'
      placeholderOverlay.style.zIndex = '10'
      ;(<HTMLElement>destinationSlide.children[0]).style.display = 'none'
      destinationSlide.appendChild(placeholderOverlay)
      this.destinationPlaceholderShowing = true
    }
  }
}
